/**
 * Per-session safety ceiling for active time. Anything above this is treated as
 * stale/orphan-inflated and clamped down. Keep in sync with the SessionTracker
 * accumulator cap and the cap migration on user_sessions.
 */
export const MAX_SESSION_SECONDS = 12 * 60 * 60; // 12h

interface SessionLike {
  started_at?: string | null;
  last_active_at?: string | null;
  ended_at?: string | null;
  duration_seconds?: number | null;
}

/**
 * Compute the active duration (in seconds) of a user_sessions row, capped to
 * MAX_SESSION_SECONDS. Prefers the explicit duration_seconds value (which the
 * client accumulates from real activity); falls back to wall-clock between
 * started_at and last_active_at / ended_at only when needed, still capped.
 */
export function getSessionDuration(s: SessionLike): number {
  if (!s.started_at) return 0;
  const startMs = new Date(s.started_at).getTime();
  const endMs = new Date(s.ended_at || s.last_active_at || s.started_at).getTime();
  const wallClock = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const stored =
    typeof s.duration_seconds === "number" && s.duration_seconds > 0
      ? s.duration_seconds
      : 0;
  // Use the stored accumulator when present, otherwise wall-clock — and never
  // exceed wall-clock (guards against client bugs writing huge values) or the
  // hard ceiling.
  const raw = stored > 0 ? Math.min(stored, wallClock || stored) : wallClock;
  return Math.min(raw, MAX_SESSION_SECONDS);
}
